/**
 * The settings nav — `docs/catalog-and-packages.md` §6.1, drawn from
 * `design/mockups/21-catalog-packages/html/packages-settings-home.html`.
 *
 * Seven groups, because a company's settings really are seven different jobs
 * and a flat list of twenty-two rows is a list nobody reads. The search box is
 * not a nicety: somebody who knows the word "markup" should not have to guess
 * that it lives under Product → Pricing & margins.
 *
 * Every row wears a glyph. Without one the nav is twenty-two lines of 13px
 * text and the eye has nothing to land on; with one, "Crews" and "Catalog" are
 * told apart before the word is read. The glyph is also what the home card and
 * the group header use, so one page is one picture wherever it is drawn.
 *
 * **Pages nobody has built yet are in the list**, with no "soon" badge. They
 * render a placeholder that says the page is not built and names what will be
 * on it. Hiding the row would make the product look as though it has no
 * opinion about tax or routing, which is a worse lie than "not yet"; badging
 * it turns the nav into a progress report on us rather than a map of the
 * company.
 */
import { NavLink } from 'react-router-dom'
import { useRef, useState, type ComponentType } from 'react'
import type { MeResponse } from '@rowted/shared'
import { can, type PermissionArea } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { useDismiss } from '../components/overlays.js'
import {
  NAV_ICONS,
  IconAccounting, IconAudit, IconBillingPlans, IconBoardDefaults, IconCatalog,
  IconCatalogSetup, IconCrews, IconVisitDay, IconDivisions, IconFeatures, IconInvoicingFees, IconMaps,
  IconOrganization, IconPortal, IconPricing, IconProfileBranding, IconSearch, IconSecurity,
  IconClose, IconEmail, IconQuote, IconSms, IconSuggestions, IconTemplates, IconTimePin, IconTransfer, IconUsersRoles,
  IconLeadSources,
  type IconProps,
} from '../frame/icons.js'

export interface SettingsPage {
  /** The path under `/settings`. An empty string is the home. */
  path: string
  label: string
  /** What it is for, shown on the home cards and in a placeholder. */
  blurb: string
  /** The glyph on its nav row. */
  icon: ComponentType<IconProps>
  /** Who may open it. The shell's own gate is `company_settings: view`. */
  area?: PermissionArea
  /** False while the page is a placeholder. Never drawn in the nav. */
  built?: boolean
}

export interface SettingsGroup {
  key: string
  label: string
  /** The sentence on the home card. */
  blurb: string
  pages: SettingsPage[]
}

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    key: 'company',
    label: 'Company',
    blurb: 'Who you are and where you work from.',
    pages: [
      { path: 'profile', label: 'Profile & branding', icon: IconProfileBranding,
        blurb: 'Name, logo, colours, what the customer sees', built: true },
      /*
       * "Organization", not "Branches & yards": the page it opens is the whole
       * org tree — company, region, district, branch — and a yard is one thing
       * that hangs off a branch rather than half the subject
       * (`company-and-organization.md` §5.3).
       */
      { path: 'organization', label: 'Organization', icon: IconOrganization,
        blurb: 'The org tree, the branches, and where stock lives', built: true },
      { path: 'divisions', label: 'Divisions', icon: IconDivisions,
        blurb: 'The lines of business a quote is split by', built: true },
    ],
  },
  {
    key: 'people',
    label: 'People',
    blurb: 'Everyone who signs in, what they can see, and how crews are organized.',
    pages: [
      { path: 'people', label: 'Users & roles', icon: IconUsersRoles,
        blurb: 'Who is here and what they can reach', area: 'employees', built: true },
      { path: 'crews', label: 'Crews', icon: IconCrews,
        blurb: 'Who works together, and out of which yard' },
      { path: 'time', label: 'Time & PIN', icon: IconTimePin,
        blurb: 'Clock rules, PIN length, device registration' },
    ],
  },
  {
    key: 'product',
    label: 'Product',
    blurb: "What's switched on, what quotes are built from, and how prices are built.",
    pages: [
      { path: 'features', label: 'Features', icon: IconFeatures,
        blurb: 'Switches for the parts of Rowted this company uses' },
      { path: 'catalog', label: 'Catalog', icon: IconCatalog,
        blurb: 'What a quote is built out of', area: 'quotes', built: true },
      /* Which kinds of visit the Kind menu offers (visits §4.5.1, Board 21). */
      { path: 'visit-kinds', label: 'Visit kinds', icon: IconVisitDay,
        blurb: 'Which kinds of visit your business uses', built: true },
      { path: 'catalog-setup', label: 'Catalog setup', icon: IconCatalogSetup,
        blurb: 'Categories, units and measurement types', built: true },
      /* The kinds of machine, and the icon every one of them wears on the
         Equipment list (`marks-and-icons.md` §3). */
      { path: 'equipment-types', label: 'Equipment types', icon: NAV_ICONS.equipment,
        blurb: 'The kinds of machine, and the icon each one wears', built: true },
      { path: 'pricing', label: 'Pricing & margins', icon: IconPricing,
        blurb: 'Default markup, target margin, rate tables' },
    ],
  },
  {
    key: 'money',
    label: 'Money',
    blurb: 'How invoices are created and paid, and how books sync.',
    pages: [
      { path: 'billing', label: 'Billing plans & taxes', icon: IconBillingPlans,
        blurb: 'How work turns into invoices, and what tax applies' },
      { path: 'invoicing', label: 'Invoicing & fees', icon: IconInvoicingFees,
        blurb: 'Terms, reminders, late fees, surcharges' },
      { path: 'accounting', label: 'QuickBooks & Stripe', icon: IconAccounting,
        blurb: 'Where the money is recorded and taken' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    blurb: 'What customers receive and what they can do themselves.',
    pages: [
      /*
       * First in the group, because it is the document a customer reads
       * before anything else is sent to them (`docs/proposal.md` §10.4).
       */
      { path: 'proposal', label: 'Proposal', icon: IconQuote,
        blurb: 'What customers read on a proposal' },
      /* Second, after Proposal (`docs/activity.md` §12.3, Board 17 E). */
      { path: 'texts-and-email', label: 'Texts & email', icon: IconSms,
        blurb: 'Where customer messages land, and how email is sent', area: 'company_settings', built: true },
      /* Beside Texts & email (Aaron, Oct 24, note 2.7): what every email a person sends ends with. */
      { path: 'email-signatures', label: 'Email signatures', icon: IconEmail,
        blurb: 'What every email a person sends ends with', area: 'company_settings', built: true },
      { path: 'templates', label: 'Notification templates', icon: IconTemplates,
        blurb: 'What an email or a text says' },
      { path: 'portal', label: 'Customer portal', icon: IconPortal,
        blurb: 'What a customer can see and do themselves' },
      /* `new-request.md` §14.4, Board 12 D. */
      { path: 'lead-sources', label: 'Lead sources', icon: IconLeadSources,
        blurb: 'where your work comes from', built: true },
    ],
  },
  {
    key: 'scheduling',
    label: 'Scheduling',
    blurb: 'How the board behaves and which suggestions apply themselves.',
    pages: [
      /* First under Scheduling (`new-request.md` §14.1, Board 11). */
      { path: 'sales-coverage', label: 'Sales coverage', icon: IconUsersRoles,
        blurb: 'who is recommended for which requests', built: true },
      { path: 'board', label: 'Board defaults', icon: IconBoardDefaults,
        blurb: 'Day length, buffers, what the board opens on' },
      { path: 'suggestions', label: 'Suggestion rules', icon: IconSuggestions,
        blurb: 'How the scheduler picks a crew and a day' },
      { path: 'maps', label: 'Maps & routing', icon: IconMaps,
        blurb: 'Drive times, zones, and the map provider' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    blurb: 'Moving data in and out, and who did what.',
    pages: [
      { path: 'transfer', label: 'Import & export', icon: IconTransfer,
        blurb: 'Bring data in, take it out, undo a batch' },
      { path: 'audit', label: 'Audit log', icon: IconAudit,
        blurb: 'Every change, who made it and when' },
      { path: 'security', label: 'Security', icon: IconSecurity,
        blurb: 'Sessions, two-step, password rules' },
    ],
  },
]

/** Every page in one list, for the shell's router and the search. */
export const SETTINGS_PAGES = SETTINGS_GROUPS.flatMap((group) => group.pages)

export function findSettingsPage(path: string): SettingsPage | undefined {
  return SETTINGS_PAGES.find((page) => page.path === path)
}

/** Whether this person may open a page at all. */
export function canOpen(me: MeResponse, page: SettingsPage): boolean {
  return page.area ? can(me.permissions, page.area, 'view') : true
}

export function SettingsNav({ me }: { me: MeResponse }) {
  return (
    <nav className={cn('flex w-[232px] shrink-0 flex-col overflow-y-auto pb-3',
      'border-r border-line bg-surface-nav')}>
      <SettingsNavList me={me} />
    </nav>
  )
}

/**
 * The search box and the links, without the column around them.
 *
 * Two surfaces draw them: the 232px rail on a desktop, and the sheet a phone
 * opens from the page bar. Same list, same search, same order — the sheet is
 * not a second, shorter menu that quietly leaves a page out.
 */
function SettingsNavList({ me, onNavigate }: {
  me: MeResponse
  /** The sheet closes itself when a link is followed. */
  onNavigate?: () => void
}) {
  const [query, setQuery] = useState('')
  const term = query.trim().toLowerCase()

  const groups = SETTINGS_GROUPS
    .map((group) => ({
      ...group,
      pages: group.pages.filter((page) => canOpen(me, page) && (
        !term
        || page.label.toLowerCase().includes(term)
        // The blurb too: somebody searching "markup" should land on Pricing.
        || page.blurb.toLowerCase().includes(term))),
    }))
    .filter((group) => group.pages.length > 0)

  return (
    <>
      <label className="relative mx-3.5 mb-1 mt-3.5 block shrink-0">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint">
          <IconSearch size={14} />
        </span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search settings"
          aria-label="Search settings"
          className={cn('h-8 w-full rounded-[9px] border border-line bg-surface pl-8 pr-2.5',
            'text-[12.5px] text-text placeholder:text-text-faint',
            'focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-ring')}
        />
      </label>

      {groups.length === 0 && (
        <p className="m-0 px-3.5 pt-3.5 text-[12.5px] text-text-faint">
          Nothing matches “{query}”.
        </p>
      )}

      {groups.map((group) => (
        <div key={group.key} className="flex flex-col">
          <span className={cn('px-3.5 pb-1 pt-3.5 text-[10.5px] font-bold uppercase',
            'tracking-[0.06em] text-text-faint')}>
            {group.label}
          </span>
          {group.pages.map((page) => (
            <NavLink
              key={page.path}
              to={`/settings/${page.path}`}
              onClick={onNavigate}
              className={({ isActive }) => cn(
                'mx-2 flex h-[34px] items-center gap-[9px] whitespace-nowrap rounded-[8px]',
                'px-2.5 text-[13px]',
                isActive
                  ? 'bg-accent-soft font-semibold text-accent-dark'
                  : 'font-medium text-text hover:bg-hover',
              )}
            >
              {({ isActive }) => (
                <>
                  <span className={cn('flex shrink-0',
                    isActive ? 'text-accent' : 'text-text-muted')}>
                    <page.icon size={15} />
                  </span>
                  <span className="truncate">{page.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </>
  )
}

/**
 * The same menu on a phone — `docs/scope-switcher.md`'s sibling problem.
 *
 * There is no 232px rail at phone width: 232 of 390 is a list of links taking
 * three-fifths of the screen. The shell's answer was that `/settings` *is* the
 * menu and the bar's back chevron gets you there — true, and it made moving
 * from Divisions to Roles a two-tap round trip through a page nobody wanted to
 * see. This is the same sheet the main nav opens, carrying the same list.
 */
export function SettingsNavSheet({ me, open, onClose }: {
  me: MeResponse
  open: boolean
  onClose: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)
  useDismiss(open, onClose, panel)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex bg-black/25">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Settings menu"
        className="flex h-full w-[272px] max-w-[82vw] flex-col bg-surface-nav shadow-slideover"
      >
        <header className={cn('flex h-header shrink-0 items-center justify-between',
          'border-b border-line pl-4 pr-2')}>
          <span className="text-card text-text">Settings</span>
          <button
            type="button"
            aria-label="Close the settings menu"
            onClick={onClose}
            className={cn('flex h-9 w-9 items-center justify-center rounded-field',
              'text-text-muted hover:bg-hover')}
          >
            <IconClose size={18} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-safe">
          <SettingsNavList me={me} onNavigate={onClose} />
        </div>
      </div>
    </div>
  )
}
